import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Card,
  CardContent,
  Grid,
  Box,
  Button,
  Paper,
  Chip,
  Avatar,
  MenuItem,
  CircularProgress,
  Alert,
  Fade,
  Tabs,
  Tab,
  Fab,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  
} from '@mui/material';
import {
  Thermostat,
  WaterDrop,
  Air,
  Speed,
  Compress,
  WbSunny,
  LocationOn,
  BugReport,
  Settings,
  Dashboard as DashboardIcon,
  Map as MapIcon,
  Add,
  TableChart,
  Search,
  FilterList,
  CompareArrows,
  Group,
  NotificationsOff,
  NotificationsActive,
  Warning,
  Tune,
  Delete,
  ExpandMore,
  ExpandLess,
  Close,
  Science,
  FolderShared,
  Sensors,
  AdminPanelSettings,
  CloudUpload,
  CheckCircle,
  ErrorOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api, { stationService, dashboardService, stationTimeseriesService, alertRuleService, filterService, groupService, TriggeredAlert, validationRulesService } from '../services/api';
import { convertToCSV, downloadCSV, generateCSVFilename } from '../utils/csvExport';
import MapView from './MapView';
import StationDetails from './StationDetails';
import AddStationDialog from './AddStationDialog';
import StationConfigDialog from './StationConfigDialog';
import StationCompare from './StationCompare';
import GroupsTab from './GroupsTab';
import { SUPPORTED_METRICS } from '../config/sensorMetrics';
import { convertValue, getDisplayUnit, getUnitOptions } from '../utils/unitConversions';

// Reverse map: display name (from API) → internal field name (for unit conversion)
const DISPLAY_TO_INTERNAL: Record<string, string> = {};
SUPPORTED_METRICS.forEach(m => {
  // The API returns keys like "temperature", "humidity", "wind_speed" etc
  // which match userFriendly names
  DISPLAY_TO_INTERNAL[m.userFriendly] = m.internal;
});

interface Location {
  latitude: number;
  longitude: number;
}

interface Station {
  uuid: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at?: string;
  location: Location;
  elevation?: number;
  notes?: string;
  owner?: string;
  public: boolean;
  source?: string;
  timezone?: string;
  status: string;
  debug: boolean;
  latest_readings?: Record<string, any>;
  is_owner?: boolean;
  tags?: string[];
  unit_preferences?: Record<string, string>;
}

interface DashboardStats {
  total_stations: number;
  active_stations: number;
  disabled_stations: number;
  debug_stations: number;
  active_alerts: number;
  last_updated: string;
  system_status: string;
  is_admin?: boolean;
  max_stations?: number;
}

interface AdvancedRule {
  id: number;
  metric: string;
  operator: '>' | '<' | '>=' | '<=' | '=';
  value: string;
}

// Metric display configuration
// Use centralized metric configuration
const metricConfig: Record<string, { name: string; color: string; unit: string }> = (() => {
  const config: Record<string, { name: string; color: string; unit: string }> = {};
  SUPPORTED_METRICS.forEach(metric => {
    config[metric.internal] = {
      name: metric.userFriendly.charAt(0).toUpperCase() + metric.userFriendly.slice(1).replace(/_/g, ' '),
      color: metric.color,
      unit: metric.unit
    };
  });
  return config;
})();

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { keycloak, initialized } = useKeycloak();
  const [stations, setStations] = useState<Station[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [tabValue, setTabValue] = useState(0);
  const [selectedStationUuid, setSelectedStationUuid] = useState<string>('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [initialDialogTab, setInitialDialogTab] = useState(0);
  const [addStationDialogOpen, setAddStationDialogOpen] = useState(false);
  const [addStationLocation, setAddStationLocation] = useState<{ lat: number; lng: number } | undefined>(undefined);
  const [selectedMapStationUuid, setSelectedMapStationUuid] = useState<string>('');
  const [selectedMapStation, setSelectedMapStation] = useState<Station | null>(null);
  const [mapTimeSeriesData, setMapTimeSeriesData] = useState<any[]>([]);
  const [mapTimeRange, setMapTimeRange] = useState<number>(24);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedConfigStation, setSelectedConfigStation] = useState<Station | undefined>(undefined);
  const [warningSnackbarOpen, setWarningSnackbarOpen] = useState(false);
  const [betaModalOpen, setBetaModalOpen] = useState(false);
  const dataFetched = useRef(false);
  const metricsDashboardRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Show beta notice modal only once (stored in localStorage)
  useEffect(() => {
    const hasSeenBetaNotice = localStorage.getItem('sencore_beta_notice_seen');
    if (!hasSeenBetaNotice) {
      setBetaModalOpen(true);
    }
  }, []);

  const handleCloseBetaModal = () => {
    localStorage.setItem('sencore_beta_notice_seen', 'true');
    setBetaModalOpen(false);
  };

  // Table View state
  const [tableData, setTableData] = useState<any[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStation, setFilterStation] = useState<string>('');
  const [filterMetric, setFilterMetric] = useState<string>('all');
  const [tableTimeRange, setTableTimeRange] = useState<number>(24);
  const [filterTag, setFilterTag] = useState<string>('all');
  const [stationView, setStationView] = useState<'my' | 'shared'>('my');
  const [sharedStations, setSharedStations] = useState<any[]>([]);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [sharedFilterGroup, setSharedFilterGroup] = useState<string>('all');

  // Bulk import state (admin only)
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState<any>(null);
  const [bulkImportError, setBulkImportError] = useState<string | null>(null);

  // Advanced filter state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedRules, setAdvancedRules] = useState<AdvancedRule[]>([]);
  const [advancedMatchMode, setAdvancedMatchMode] = useState<'all' | 'any'>('all');
  const [nextRuleId, setNextRuleId] = useState(1);
  const [advancedFilterLoading, setAdvancedFilterLoading] = useState(false);
  const [advancedFilterResults, setAdvancedFilterResults] = useState<any[] | null>(null);
  const [advancedFilterError, setAdvancedFilterError] = useState<string | null>(null);

  // Alert Rules state - stores station UUIDs that have triggered alerts
  const [stationAlerts, setStationAlerts] = useState<Map<string, TriggeredAlert[]>>(new Map());

  // Get user info from Keycloak token
  const userEmail = keycloak.tokenParsed?.email || keycloak.tokenParsed?.preferred_username || 'User';

  const handleMapTimeRangeChange = (event: any) => {
    setMapTimeRange(event.target.value);
  };

  // Bulk import handler (admin only)
  const handleBulkImport = async (file: File) => {
    setBulkImportLoading(true);
    setBulkImportResult(null);
    setBulkImportError(null);

    // Client-side CSV check
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setBulkImportError('Only CSV files are accepted.');
      setBulkImportLoading(false);
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      setBulkImportError('File too large. Maximum size is 1MB.');
      setBulkImportLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/stations/bulk-import/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkImportResult(response.data);
      // Refresh stations list (own stations only, same as initial fetch)
      const stationsData = await stationService.getAll({ include_public: false });
      setStations(stationsData);
      const statsData = await dashboardService.getStats();
      setStats(statsData);
    } catch (err: any) {
      setBulkImportError(err.response?.data?.error || 'Import failed. Please check your CSV file.');
      if (err.response?.data?.errors) {
        setBulkImportResult({ errors: err.response.data.errors, total_rows: err.response.data.total_rows });
      }
    } finally {
      setBulkImportLoading(false);
    }
  };

  const getMapTimeRangeLabel = () => {
    if (mapTimeRange === 24) return '24-Hour';
    if (mapTimeRange === 48) return '48-Hour';
    if (mapTimeRange === 120) return '5-Day';
    if (mapTimeRange === 720) return 'Monthly';
    return `${mapTimeRange}-Hour`;
  };

  useEffect(() => {
    // Wait for Keycloak to initialize
    if (!initialized) {
      return;
    }

    // Check if user is authenticated via Keycloak
    if (!keycloak.authenticated) {
      navigate('/login');
      return;
    }

    // Prevent duplicate calls by checking if data has already been fetched
    if (!dataFetched.current) {
      fetchData();
    }
  }, [initialized, keycloak.authenticated]); // Depend on Keycloak state

  const fetchData = async () => {
    try {
      const [stationsData, statsData] = await Promise.all([
        // Only fetch user's own stations (include_public: false for security)
        stationService.getAll({ include_public: false }),
        dashboardService.getStats()
      ]);

      setStations(stationsData);
      setStats(statsData);
      dataFetched.current = true; // Mark data as fetched

      // Fetch triggered alerts from backend (efficient - checks last 48h in database)
      try {
        const triggeredAlerts = await validationRulesService.getTriggeredAlerts();
        const alertsMap = new Map<string, any[]>();

        // Group alerts by station UUID
        for (const alert of triggeredAlerts) {
          const uuid = alert.station_uuid;
          const existingAlerts = alertsMap.get(uuid) || [];
          existingAlerts.push(alert);
          alertsMap.set(uuid, existingAlerts);
        }

        setStationAlerts(alertsMap);
      } catch (alertErr) {
        console.warn('Could not fetch triggered alerts:', alertErr);
      }
    } catch (err) {
      setError('Network error while fetching data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSharedStations = async () => {
    setSharedLoading(true);
    try {
      const data = await groupService.sharedWithMe();
      setSharedStations(data);
    } catch (err) {
      console.error('Failed to load shared stations:', err);
    } finally {
      setSharedLoading(false);
    }
  };

  // Fetch shared stations when switching to shared view
  useEffect(() => {
    if (stationView === 'shared' && sharedStations.length === 0) {
      fetchSharedStations();
    }
  }, [stationView]);

  // Refetch map time series data when time range changes
  useEffect(() => {
    if (selectedMapStationUuid && tabValue === 1) {
      fetchMapTimeSeriesData(selectedMapStationUuid);
    }
  }, [mapTimeRange]);

  const handleLogout = () => {
    // Logout from Keycloak
    keycloak.logout({
      redirectUri: window.location.origin
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Clear bulk import state when leaving the admin tab
    if (newValue !== 5) {
      setBulkImportResult(null);
      setBulkImportError(null);
    }
  };

  const handleStationSelect = (stationUuid: string, tab: number = 0) => {
    setSelectedStationUuid(stationUuid);
    setInitialDialogTab(tab);
    setDetailsDialogOpen(true);
  };

  const handleMapStationSelect = useCallback(async (stationUuid: string) => {
    setSelectedMapStationUuid(stationUuid);
    await Promise.all([
      fetchMapStationDetails(stationUuid),
      fetchMapTimeSeriesData(stationUuid)
    ]);
  }, []);

  const handleScrollToTimeSeries = useCallback(() => {
    metricsDashboardRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, []);

  const fetchMapStationDetails = async (stationUuid: string) => {
    try {
      const data = await stationService.getById(stationUuid);
      setSelectedMapStation(data);
    } catch (error) {
      console.error('Error fetching map station details:', error);
    }
  };

  // Keep a ref so the memoised handleMapStationSelect always reads the latest value
  const mapTimeRangeRef = useRef(mapTimeRange);
  mapTimeRangeRef.current = mapTimeRange;

  const fetchMapTimeSeriesData = async (stationUuid: string) => {
    try {
      const data = await stationTimeseriesService.getTimeseries(stationUuid, mapTimeRangeRef.current);
      const formattedData = data.timeseries.map((point: any) => ({
          ...point,
          timestampMs: new Date(point.timestamp).getTime()
        }));
        setMapTimeSeriesData(formattedData);
    } catch (error) {
      console.error('Error fetching map time series data:', error);
    }
  };

  const handleAddStation = (location: { lat: number; lng: number }) => {
    setAddStationLocation(location);
    setAddStationDialogOpen(true);
  };

  const handleCreateStation = async (stationData: any): Promise<boolean> => {
    try {
      await stationService.create(stationData);
      fetchData(); // Refresh the data
      return true;
    } catch (error: any) {
      console.error('Error creating station:', error);
      throw error;
    }
  };

  const handleEditStation = async (stationUuid: string, stationData: Partial<Station>) => {
    try {
      // Call the API to update the station
      await stationService.update(stationUuid, stationData);
      console.log('Station updated successfully');
      
      // Refresh data after edit
      await fetchData();
      setConfigDialogOpen(false);
    } catch (error) {
      console.error('Error editing station:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleDeleteStation = async (stationUuid: string, withExport: boolean) => {
    try {
      if (withExport) {
        // Export to CSV first before deletion
        await handleExportStation(stationUuid);
        // Give a small delay to ensure export completes
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Call the API to delete the station
      await stationService.delete(stationUuid);
      console.log('Station deleted successfully:', stationUuid);
      
      // Refresh data after deletion
      await fetchData();
      setConfigDialogOpen(false);
    } catch (error) {
      console.error('Error deleting station:', error);
    }
  };

  const handleExportStation = async (stationUuid: string) => {
    try {
      const station = stations.find(s => s.uuid === stationUuid);
      if (!station) {
        console.error('Station not found');
        return;
      }

      // Get timeseries data for the last 30 days
      const timeseriesData = await stationTimeseriesService.getTimeseries(station.uuid, 24 * 30);
      
      // Convert to CSV format
      const csvData = convertToCSV({
        id: station.uuid,
        name: station.name,
        description: station.description,
        timeseries: timeseriesData.timeseries,
        period_hours: timeseriesData.period_hours,
        data_points: timeseriesData.data_points
      });
      
      // Download CSV file
      const filename = generateCSVFilename(station.name);
      downloadCSV(csvData, filename);
      
      console.log('Station data exported to CSV:', filename);
    } catch (error) {
      console.error('Error exporting station data:', error);
    }
    setConfigDialogOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'success';
      case 'debug': return 'warning';
      case 'disabled': return 'error';
      default: return 'default';
    }
  };

  const getSensorIcon = (sensorType: string) => {
    switch (sensorType.toLowerCase()) {
      case 'temperature': return <Thermostat />;
      case 'humidity': 
      case 'rain': 
      case 'reference_et': return <WaterDrop />;
      case 'wind_speed': return <Air />;
      case 'atmospheric_pressure': return <Compress />;
      case 'solar_radiation': return <WbSunny />;
      default: return <Speed />;
    }
  };

  const formatReading = (key: string, reading: any) => {
    const sensorNames: Record<string, string> = {
      temperature: 'Temperature',
      humidity: 'Humidity',
      rain: 'Rainfall',
      wind_speed: 'Wind Speed',
      wind_direction: 'Wind Direction',
      atmospheric_pressure: 'Pressure',
      reference_et: 'Reference ET',
      solar_radiation: 'Solar Radiation'
    };
    return sensorNames[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  // Table View handlers
  const fetchTableData = async () => {
    if (!filterStation) {
      setTableData([]);
      return;
    }
    const station = stations.find(s => s.uuid === filterStation);
    if (!station) {
      setTableData([]);
      return;
    }
    setTableLoading(true);
    try {
      const stationData: any = await stationTimeseriesService.getTimeseries(filterStation, tableTimeRange);
      const unitPrefs = station.unit_preferences;
      const stationName = station.name || stationData.station?.name || filterStation;
      const allData: any[] = [];

      (stationData.timeseries || []).forEach((point: any) => {
        Object.keys(point).forEach(key => {
          if (key !== 'timestamp' && point[key] !== null && point[key] !== undefined) {
            const config = metricConfig[key];
            if (config) {
              const hasConv = unitPrefs && unitPrefs[key] && getUnitOptions(key);
              allData.push({
                stationUuid: filterStation,
                stationName,
                timestamp: point.timestamp,
                metric: key,
                metricName: config.name,
                value: hasConv ? convertValue(point[key], key, unitPrefs![key]) : point[key],
                unit: hasConv ? getDisplayUnit(key, unitPrefs) : config.unit,
              });
            }
          }
        });
      });

      // Sort by timestamp descending (newest first)
      allData.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      setTableData(allData);
    } catch (error) {
      console.error('Error fetching table data:', error);
      setTableData([]);
    } finally {
      setTableLoading(false);
    }
  };

  // Fetch table data when a station is selected or time range changes
  useEffect(() => {
    if (tabValue === 2 && filterStation && stations.length > 0) {
      fetchTableData();
    } else if (tabValue === 2 && !filterStation) {
      setTableData([]);
    }
  }, [tabValue, tableTimeRange, filterStation, stations]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterStationChange = (event: any) => {
    setFilterStation(event.target.value);
    setPage(0);
  };

  const handleFilterMetricChange = (event: any) => {
    setFilterMetric(event.target.value);
    setPage(0);
  };

  const handleTableTimeRangeChange = (event: any) => {
    setTableTimeRange(event.target.value);
    setPage(0);
  };

  // Advanced filter handlers
  const handleAddAdvancedRule = () => {
    const defaultMetric = getUniqueMetrics()[0] || 'temperature_celsius';
    setAdvancedRules([
      ...advancedRules,
      { id: nextRuleId, metric: defaultMetric, operator: '>', value: '' }
    ]);
    setNextRuleId(nextRuleId + 1);
  };

  const handleRemoveAdvancedRule = (ruleId: number) => {
    setAdvancedRules(advancedRules.filter(rule => rule.id !== ruleId));
  };

  const handleAdvancedRuleChange = (ruleId: number, field: keyof AdvancedRule, value: string) => {
    setAdvancedRules(advancedRules.map(rule =>
      rule.id === ruleId ? { ...rule, [field]: value } : rule
    ));
  };

  const handleAdvancedMatchModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'all' | 'any' | null) => {
    if (newMode !== null) {
      setAdvancedMatchMode(newMode);
    }
  };

  // Auto re-apply filters when rules or match mode change (only if user already applied once)
  useEffect(() => {
    if (advancedFilterResults === null) return;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => applyAdvancedFilters(), 500);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [advancedRules, advancedMatchMode]);

  const clearAdvancedFilters = () => {
    setAdvancedRules([]);
    setAdvancedFilterResults(null);
    setAdvancedFilterError(null);
  };

  const applyAdvancedFilters = async () => {
    // Validate all rules have values
    const validRules = advancedRules.filter(r => r.value !== '' && !isNaN(parseFloat(r.value)));
    if (validRules.length === 0) {
      setAdvancedFilterError('Please add at least one valid rule');
      return;
    }

    setAdvancedFilterLoading(true);
    setAdvancedFilterError(null);
    setPage(0);
    try {
      const result = await filterService.filter({
        rules: validRules.map(r => ({
          metric: r.metric,
          operator: r.operator,
          value: parseFloat(r.value),
        })),
        match_mode: advancedMatchMode,
        hours: tableTimeRange,
        limit: 500,
      });

      // Convert backend response to tableData format
      const stationNameMap = new Map(stations.map(s => [s.uuid, s.name]));
      const converted: any[] = [];
      result.timeseries.forEach((point: any) => {
        Object.keys(point).forEach(key => {
          if (key !== 'timestamp' && key !== 'station_uuid' && key !== 'station_name' && point[key] !== null && point[key] !== undefined) {
            const config = metricConfig[key];
            if (config) {
              converted.push({
                stationUuid: point.station_uuid,
                stationName: stationNameMap.get(point.station_uuid) || point.station_name,
                timestamp: point.timestamp,
                metric: key,
                metricName: config.name,
                value: point[key],
                unit: config.unit,
              });
            }
          }
        });
      });
      converted.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setAdvancedFilterResults(converted);
    } catch (err: any) {
      setAdvancedFilterError(err?.response?.data?.error || 'Failed to apply filters');
    } finally {
      setAdvancedFilterLoading(false);
    }
  };

  // Filter table data based on filters and search
  const getFilteredTableData = () => {
    // If backend filtering is active, use those results
    const sourceData = advancedFilterResults !== null ? advancedFilterResults : tableData;

    return sourceData.filter(row => {
      // Filter by station (data is already fetched per selected station,
      // but keep this check in case of stale state)
      if (filterStation && row.stationUuid !== filterStation) {
        return false;
      }

      // Filter by metric
      if (filterMetric !== 'all' && row.metric !== filterMetric) {
        return false;
      }

      return true;
    });
  };

  // Get unique metrics from table data
  const getUniqueMetrics = () => {
    const metrics = new Set(tableData.map(row => row.metric));
    return Array.from(metrics);
  };

  // Get all unique tags from all stations
  const getAllTags = () => {
    const allTags = new Set<string>();
    stations.forEach(station => {
      if (station.tags && Array.isArray(station.tags)) {
        station.tags.forEach(tag => allTags.add(tag));
      }
    });
    return Array.from(allTags).sort();
  };

  // Filter stations by selected tag
  const getFilteredStations = () => {
    if (filterTag === 'all') {
      return stations;
    }
    return stations.filter(station =>
      station.tags && Array.isArray(station.tags) && station.tags.includes(filterTag)
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading SenCore Dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Modern Navigation Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.95) 0%, rgba(22, 33, 62, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0, 212, 255, 0.1)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, justifyContent: 'center' }}>
            <Typography variant="h6" component="div" sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #00d4ff 0%, #ff6b6b 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}>
              SenCore Platform Environmental Monitoring System
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {/* Modern Navigation Tabs */}
        <Box sx={{ 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.8) 0%, rgba(22, 33, 62, 0.8) 100%)',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.05)',
          p: 1
        }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#00d4ff',
                height: 4,
                borderRadius: 2,
                bottom: 8,
              },
              '& .MuiTab-root': {
                borderRadius: 2,
                margin: 0.5,
                minHeight: 56,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(0, 212, 255, 0.1)',
                  color: '#00d4ff',
                },
              },
            }}
          >
            <Tab
              icon={<DashboardIcon />}
              label="Dashboard"
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              icon={<MapIcon />}
              label="Map View"
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              icon={<TableChart />}
              label="Table View"
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              icon={<CompareArrows />}
              label="Compare"
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              icon={<Group />}
              label="Groups"
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            {stats?.is_admin && (
              <Tab
                icon={<AdminPanelSettings />}
                label="Advanced Tasks"
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
            )}
          </Tabs>
        </Box>

        {/* Dashboard Content */}
        {tabValue === 0 && (
          <>
            {/* Modern Dashboard Stats */}
            {stats && (
              <Fade in timeout={1000}>
                <Grid container spacing={3} sx={{ mb: 5 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 153, 204, 0.05) 100%)',
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                      position: 'relative',
                      overflow: 'visible',
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            background: 'linear-gradient(135deg, #00d4ff, #33dcff)',
                            mr: 2 
                          }}>
                            <DashboardIcon sx={{ color: '#000', fontSize: 20 }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Total Stations
                          </Typography>
                        </Box>
                        <Typography variant="h3" sx={{
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #00d4ff, #33dcff)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          color: 'transparent',
                          mb: 1
                        }}>
                          {stats.total_stations || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Active monitoring points
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, rgba(0, 245, 160, 0.1) 0%, rgba(0, 200, 118, 0.05) 100%)',
                      border: '1px solid rgba(0, 245, 160, 0.2)',
                      position: 'relative',
                      overflow: 'visible',
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            background: 'linear-gradient(135deg, #00f5a0, #4fffb4)',
                            mr: 2 
                          }}>
                            <WbSunny sx={{ color: '#000', fontSize: 20 }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Online Stations
                          </Typography>
                        </Box>
                        <Typography variant="h3" sx={{
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #00f5a0, #4fffb4)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          color: 'transparent',
                          mb: 1
                        }}>
                          {stats.active_stations || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Operational systems
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, rgba(55, 66, 250, 0.1) 0%, rgba(83, 82, 237, 0.05) 100%)',
                      border: '1px solid rgba(55, 66, 250, 0.2)',
                      position: 'relative',
                      overflow: 'visible',
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #3742fa, #5352ed)',
                            mr: 2
                          }}>
                            <BugReport sx={{ color: '#fff', fontSize: 20 }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Debug Stations
                          </Typography>
                        </Box>
                        <Typography variant="h3" sx={{
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #3742fa, #5352ed)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          color: 'transparent',
                          mb: 1
                        }}>
                          {stats.debug_stations || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          In test mode
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    {(() => {
                      // Calculate total triggered alerts
                      let totalAlerts = 0;
                      stationAlerts.forEach((alerts) => {
                        totalAlerts += alerts.length;
                      });
                      const hasAlerts = totalAlerts > 0;

                      return (
                        <Card sx={{
                          background: hasAlerts
                            ? 'linear-gradient(135deg, rgba(255, 107, 107, 0.1) 0%, rgba(220, 53, 69, 0.05) 100%)'
                            : 'linear-gradient(135deg, rgba(0, 245, 160, 0.1) 0%, rgba(0, 200, 118, 0.05) 100%)',
                          border: hasAlerts
                            ? '1px solid rgba(255, 107, 107, 0.2)'
                            : '1px solid rgba(0, 245, 160, 0.2)',
                          position: 'relative',
                          overflow: 'visible',
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{
                                p: 1.5,
                                borderRadius: 2,
                                background: hasAlerts
                                  ? 'linear-gradient(135deg, #ff6b6b, #dc3545)'
                                  : 'linear-gradient(135deg, #00f5a0, #4fffb4)',
                                mr: 2
                              }}>
                                {hasAlerts
                                  ? <NotificationsActive sx={{ color: '#fff', fontSize: 20 }} />
                                  : <NotificationsOff sx={{ color: '#000', fontSize: 20 }} />
                                }
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Active Alerts
                              </Typography>
                            </Box>
                            <Typography variant="h3" sx={{
                              fontWeight: 700,
                              background: hasAlerts
                                ? 'linear-gradient(135deg, #ff6b6b, #dc3545)'
                                : 'linear-gradient(135deg, #00f5a0, #4fffb4)',
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              color: 'transparent',
                              mb: 1
                            }}>
                              {totalAlerts}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {hasAlerts
                                ? `${stationAlerts.size} station${stationAlerts.size > 1 ? 's' : ''} affected`
                                : 'All systems normal'
                              }
                            </Typography>
                          </CardContent>
                        </Card>
                      );
                    })()}
                  </Grid>
                </Grid>
              </Fade>
            )}

            {/* Modern Section Header */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start', 
              mb: 4,
              p: 4,
              background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.02) 0%, rgba(255, 107, 107, 0.02) 100%)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.05)',
            }}>
              <Box>
                <Typography variant="h3" sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #00d4ff 0%, #ff6b6b 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  mb: 1
                }}>
                  Environmental Stations
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Real-time monitoring network with UUID-based data ingestion
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={`${stations.length} Active`} 
                    size="small" 
                    sx={{ 
                      backgroundColor: 'rgba(0, 245, 160, 0.1)',
                      color: '#00f5a0',
                      border: '1px solid rgba(0, 245, 160, 0.3)',
                      fontWeight: 500
                    }}
                  />
                  <Chip 
                    label="Real-time" 
                    size="small" 
                    sx={{ 
                      backgroundColor: 'rgba(0, 212, 255, 0.1)',
                      color: '#00d4ff',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      fontWeight: 500
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {stationView === 'my' && (
                <>
                <Button
                  variant="outlined"
                  startIcon={<Settings />}
                  onClick={() => {
                    if (!selectedStationUuid) {
                      setWarningSnackbarOpen(true);
                      return;
                    }
                    setSelectedConfigStation(stations.find(s => s.uuid === selectedStationUuid));
                    setConfigDialogOpen(true);
                  }}
                  sx={{ 
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: 'rgba(0, 212, 255, 0.5)',
                      backgroundColor: 'rgba(0, 212, 255, 0.04)',
                    }
                  }}
                >
                  Configure
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setAddStationDialogOpen(true)}
                  sx={{ 
                    background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
                    boxShadow: '0 8px 25px -8px rgba(0, 212, 255, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #33dcff 0%, #00b8e6 100%)',
                      boxShadow: '0 12px 35px -8px rgba(0, 212, 255, 0.4)',
                    }
                  }}
                >
                  Add Station
                </Button>
                </>
                )}
              </Box>
            </Box>

            {/* Station View Toggle + Tag Filter */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <ToggleButtonGroup
                value={stationView}
                exclusive
                onChange={(_, val) => { if (val) setStationView(val); }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(0, 212, 255, 0.15)',
                      color: '#00d4ff',
                      '&:hover': { backgroundColor: 'rgba(0, 212, 255, 0.25)' },
                    },
                  },
                }}
              >
                <ToggleButton value="my">
                  <Sensors sx={{ mr: 1, fontSize: 18 }} />
                  My Stations
                </ToggleButton>
                <ToggleButton value="shared">
                  <FolderShared sx={{ mr: 1, fontSize: 18 }} />
                  Shared with me
                </ToggleButton>
              </ToggleButtonGroup>

              {stationView === 'my' ? (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="filter-tag-label">Filter by Tag</InputLabel>
                <Select
                  labelId="filter-tag-label"
                  id="filter-tag-select"
                  value={filterTag}
                  onChange={(e) => setFilterTag(e.target.value)}
                  label="Filter by Tag"
                >
                  <MenuItem value="all">All Tags</MenuItem>
                  {getAllTags().map(tag => (
                    <MenuItem key={tag} value={tag}>
                      {tag}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              ) : sharedStations.length > 0 && (() => {
                const groupMap = new Map<string, string>();
                sharedStations.forEach((s: any) => {
                  if (s.groups && Array.isArray(s.groups)) {
                    s.groups.forEach((g: any) => groupMap.set(String(g.group_id), g.group_name));
                  }
                });
                return groupMap.size > 0 ? (
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="shared-filter-group-label">Filter by Group</InputLabel>
                    <Select
                      labelId="shared-filter-group-label"
                      value={sharedFilterGroup}
                      onChange={(e) => setSharedFilterGroup(e.target.value)}
                      label="Filter by Group"
                    >
                      <MenuItem value="all">All Groups</MenuItem>
                      {Array.from(groupMap.entries()).map(([id, name]) => (
                        <MenuItem key={id} value={id}>{name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null;
              })()}
            </Box>

        {stationView === 'my' ? (
        <>
        <Grid container spacing={3}>
          {getFilteredStations().map((station, index) => (
            <Grid item xs={12} md={6} lg={4} key={station.uuid}>
              <Fade in timeout={1000 + index * 200}>
                <Paper
                  elevation={2}
                  onClick={() => setSelectedStationUuid(station.uuid)}
                  sx={{
                    p: 3,
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: selectedStationUuid === station.uuid ? '2px solid #00d4ff' : '2px solid transparent',
                    backgroundColor: selectedStationUuid === station.uuid ? 'rgba(0, 212, 255, 0.05)' : 'background.paper',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      elevation: 4,
                      borderColor: selectedStationUuid === station.uuid ? '#00d4ff' : 'rgba(0, 212, 255, 0.3)'
                    }
                  }}
                >
                  {/* Station Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0, mr: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        {station.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {station.description}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'nowrap', flexShrink: 0 }}>
                      {/* Alert indicator */}
                      {stationAlerts.has(station.uuid) && stationAlerts.get(station.uuid)!.length > 0 && (
                        <Chip
                          icon={<Warning />}
                          label={`${stationAlerts.get(station.uuid)!.length} Alert${stationAlerts.get(station.uuid)!.length > 1 ? 's' : ''}`}
                          size="small"
                          color="error"
                          variant="filled"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedStationUuid(station.uuid);
                            setInitialDialogTab(5); // Open Alerts tab
                            setDetailsDialogOpen(true);
                          }}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: '#d32f2f',
                            color: '#fff',
                            '& .MuiChip-icon': { color: '#fff' },
                          }}
                        />
                      )}
                      {station.debug ? (
                        <Chip
                          icon={<BugReport />}
                          label="Debug"
                          size="small"
                          color="info"
                        />
                      ) : (
                        <Chip
                          label={station.status}
                          size="small"
                          color={getStatusColor(station.status) as any}
                        />
                      )}
                      <Chip
                        label={station.public ? 'Public' : 'Private'}
                        size="small"
                        color={station.public ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </Box>
                  </Box>

                  {/* Location and Details */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOn sx={{ fontSize: 16, mr: 0.5 }} color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {station.location?.latitude?.toFixed(4) || 'N/A'}, {station.location?.longitude?.toFixed(4) || 'N/A'}
                        {station.elevation && ` (${station.elevation}m)`}
                      </Typography>
                    </Box>
                    
                    <Typography variant="caption" color="text.secondary">
                      UUID: {station.uuid}
                    </Typography>
                  </Box>

                  {/* Latest Readings */}
                  {station.latest_readings && Object.keys(station.latest_readings).length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Latest Readings:
                      </Typography>
                      <Grid container spacing={1}>
                        {Object.entries(station.latest_readings).slice(0, 4).map(([sensor, reading]: [string, any]) => (
                          <Grid item xs={6} key={sensor}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              p: 1, 
                              bgcolor: 'background.default', 
                              borderRadius: 1 
                            }}>
                              <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.light' }}>
                                {React.cloneElement(getSensorIcon(sensor), { sx: { fontSize: 14 } })}
                              </Avatar>
                              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {formatReading(sensor, reading)}
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" noWrap>
                                  {(() => {
                                    const internalField = DISPLAY_TO_INTERNAL[sensor] || '';
                                    const unitPrefs = station.unit_preferences;
                                    if (typeof reading.value === 'number' && internalField && unitPrefs && unitPrefs[internalField]) {
                                      const converted = convertValue(reading.value, internalField, unitPrefs[internalField]);
                                      return `${converted.toFixed(2)} ${getDisplayUnit(internalField, unitPrefs)}`;
                                    }
                                    return `${typeof reading.value === 'number' ? reading.value.toFixed(2) : reading.value} ${reading.unit}`;
                                  })()}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Tags */}
                  {station.tags && station.tags.length > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {station.tags.map((tag, idx) => (
                        <Chip
                          key={idx}
                          label={tag}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0, 212, 255, 0.1)',
                            color: '#00d4ff',
                            border: '1px solid rgba(0, 212, 255, 0.3)',
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                      onClick={() => handleStationSelect(station.uuid)}
                    >
                      View Details
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => handleStationSelect(station.uuid, 2)}
                    >
                      Time Series
                    </Button>
                  </Box>
                </Paper>
              </Fade>
            </Grid>
          ))}
        </Grid>

            {stations.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No stations found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure your first environmental monitoring station
                </Typography>
                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={() => setAddStationDialogOpen(true)}
                >
                  Add Station
                </Button>
              </Box>
            )}
          </>
        ) : (
          /* Shared Stations View */
          sharedLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#00d4ff' }} />
            </Box>
          ) : sharedStations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <FolderShared sx={{ fontSize: 48, color: 'rgba(255,255,255,0.15)', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No shared stations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                When someone adds a station to a group you belong to, it will appear here.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {sharedStations.filter((s: any) =>
                sharedFilterGroup === 'all' || (s.groups && s.groups.some((g: any) => String(g.group_id) === sharedFilterGroup))
              ).map((station: any, index: number) => (
                <Grid item xs={12} md={6} lg={4} key={station.station_uuid}>
                  <Fade in timeout={1000 + index * 200}>
                    <Paper
                      elevation={2}
                      onClick={() => {
                        setSelectedStationUuid(station.station_uuid);
                        setInitialDialogTab(0);
                        setDetailsDialogOpen(true);
                      }}
                      sx={{
                        p: 3,
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        border: '2px solid transparent',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          borderColor: 'rgba(76, 175, 80, 0.3)',
                        }
                      }}
                    >
                      {/* Station Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flexGrow: 1, minWidth: 0, mr: 2 }}>
                          <Typography variant="h6" gutterBottom>
                            {station.station_name}
                          </Typography>
                          {station.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {station.description}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'nowrap', flexShrink: 0 }}>
                          <Chip
                            label="Shared"
                            size="small"
                            icon={<FolderShared sx={{ fontSize: 14 }} />}
                            sx={{
                              backgroundColor: 'rgba(76, 175, 80, 0.15)',
                              color: '#4caf50',
                              border: '1px solid rgba(76, 175, 80, 0.3)',
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Location */}
                      {station.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <LocationOn sx={{ fontSize: 16, mr: 0.5 }} color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {station.location.lat.toFixed(4)}, {station.location.lng.toFixed(4)}
                            {station.elevation ? ` (${station.elevation}m)` : ''}
                          </Typography>
                        </Box>
                      )}

                      {/* Latest Readings */}
                      {station.latest_readings && Object.keys(station.latest_readings).length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Latest Readings:
                          </Typography>
                          <Grid container spacing={1}>
                            {Object.entries(station.latest_readings).slice(0, 4).map(([sensor, reading]: [string, any]) => (
                              <Grid item xs={6} key={sensor}>
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  p: 1,
                                  bgcolor: 'background.default',
                                  borderRadius: 1
                                }}>
                                  <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.light' }}>
                                    {React.cloneElement(getSensorIcon(sensor), { sx: { fontSize: 14 } })}
                                  </Avatar>
                                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {formatReading(sensor, reading)}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold" noWrap>
                                      {typeof reading.value === 'number' ? reading.value.toFixed(2) : reading.value} {reading.unit}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}

                      {/* Groups */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                        {station.groups?.map((g: any) => (
                          <Chip
                            key={g.group_id}
                            icon={<Group sx={{ fontSize: 14 }} />}
                            label={g.group_name}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(0, 212, 255, 0.1)',
                              color: '#00d4ff',
                              border: '1px solid rgba(0, 212, 255, 0.3)',
                              fontSize: '0.75rem',
                            }}
                          />
                        ))}
                      </Box>

                      {/* Action Buttons - view only */}
                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ mr: 1 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedStationUuid(station.station_uuid);
                            setInitialDialogTab(0);
                            setDetailsDialogOpen(true);
                          }}
                        >
                          View Details
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedStationUuid(station.station_uuid);
                            setInitialDialogTab(2);
                            setDetailsDialogOpen(true);
                          }}
                        >
                          Time Series
                        </Button>
                      </Box>
                    </Paper>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          )
        )}
          </>
        )}

        {/* Map View */}
        {tabValue === 1 && (
          <Box sx={{ position: 'relative' }}>
            <MapView
              onStationSelect={handleMapStationSelect}
              onScrollToTimeSeries={handleScrollToTimeSeries}
            />
            
            {/* Station Metrics Display Below Map */}
            {selectedMapStation && (
              <Box ref={metricsDashboardRef} sx={{ mt: 4 }}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.8) 0%, rgba(22, 33, 62, 0.8) 100%)',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ 
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #00d4ff 0%, #ff6b6b 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                    }}>
                      {selectedMapStation.name} - Metrics Dashboard
                    </Typography>
                    
                    {/* Current Readings removed — shown in map popup on station click */}

                    {/* Time Series Charts - Dynamic */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Typography variant="h6">
                          {getMapTimeRangeLabel()} Sensor Measurements
                        </Typography>
                        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                          <InputLabel id="map-time-range-label">Time Range</InputLabel>
                          <Select
                            labelId="map-time-range-label"
                            id="map-time-range-select"
                            value={mapTimeRange}
                            onChange={handleMapTimeRangeChange}
                            label="Time Range"
                          >
                            <MenuItem value={24}>Last 24 Hours</MenuItem>
                            <MenuItem value={48}>Last 48 Hours</MenuItem>
                            <MenuItem value={120}>Last 5 Days</MenuItem>
                            <MenuItem value={720}>Last Month</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                      {mapTimeSeriesData.length > 0 ? (
                        <Box>
                          {(() => {
                            const availableMetrics = Object.keys(mapTimeSeriesData[0]).filter(
                              key => key !== 'timestamp' && key !== 'time' && key !== 'timestampMs' &&
                                     mapTimeSeriesData[0][key] !== null &&
                                     mapTimeSeriesData[0][key] !== undefined
                            );

                            // Default colors for metrics without configuration
                            const defaultColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a28bd4', '#58d4ff'];

                            return availableMetrics.map((metric, index) => {
                              const config = metricConfig[metric] || {
                                name: metric.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                                color: defaultColors[index % defaultColors.length],
                                unit: ''
                              };

                              // Apply unit conversion
                              const unitPrefs = selectedMapStation?.unit_preferences;
                              const displayUnit = getDisplayUnit(metric, unitPrefs);
                              const hasConversion = getUnitOptions(metric) !== null;
                              const chartUnit = hasConversion ? displayUnit : config.unit;

                              const chartData = hasConversion && unitPrefs && unitPrefs[metric]
                                ? mapTimeSeriesData.map(point => ({
                                    ...point,
                                    [metric]: point[metric] != null ? convertValue(point[metric], metric, unitPrefs[metric]) : null,
                                  }))
                                : mapTimeSeriesData;

                              return (
                                <Box key={metric} sx={{ mb: index < availableMetrics.length - 1 ? 4 : 0 }}>
                                  <Typography variant="h6" gutterBottom>
                                    {config.name} {chartUnit && `(${chartUnit})`}
                                  </Typography>
                                  <Box sx={{ height: 300 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                      <LineChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                          dataKey="timestampMs"
                                          type="number"
                                          scale="time"
                                          domain={['dataMin', 'dataMax']}
                                          tickFormatter={(ts) => new Date(ts).toLocaleTimeString('en-US', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit'
                                          })}
                                        />
                                        <YAxis />
                                        <Tooltip
                                          contentStyle={{
                                            backgroundColor: '#333333',
                                            border: '1px solid #555555',
                                            borderRadius: '4px',
                                            color: '#ffffff'
                                          }}
                                          labelStyle={{ color: '#ffffff' }}
                                          itemStyle={{ color: '#ffffff' }}
                                          labelFormatter={(ts) => new Date(ts).toLocaleString('en-GB', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit'
                                          })}
                                        />
                                        <Line
                                          type="monotone"
                                          dataKey={metric}
                                          stroke={config.color}
                                          strokeWidth={2}
                                          name={chartUnit ? `${config.name} (${chartUnit})` : config.name}
                                        />
                                      </LineChart>
                                    </ResponsiveContainer>
                                  </Box>
                                </Box>
                              );
                            });
                          })()}
                        </Box>
                      ) : (
                        <Typography color="text.secondary">
                          No time series data available for this station.
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>
        )}

        {/* Table View Content */}
        {tabValue === 2 && (
          <Box>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                {/* Filters */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="filter-station-label">Select Station</InputLabel>
                      <Select
                        labelId="filter-station-label"
                        id="filter-station-select"
                        value={filterStation}
                        onChange={handleFilterStationChange}
                        label="Select Station"
                      >
                        {stations.map(station => (
                          <MenuItem key={station.uuid} value={station.uuid}>
                            {station.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={2.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="filter-metric-label">Metric</InputLabel>
                      <Select
                        labelId="filter-metric-label"
                        id="filter-metric-select"
                        value={filterMetric}
                        onChange={handleFilterMetricChange}
                        label="Metric"
                      >
                        <MenuItem value="all">All Metrics</MenuItem>
                        {getUniqueMetrics().map(metric => {
                          const config = metricConfig[metric];
                          return config ? (
                            <MenuItem key={metric} value={metric}>
                              {config.name}
                            </MenuItem>
                          ) : null;
                        })}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={2.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="table-time-range-label">Time Range</InputLabel>
                      <Select
                        labelId="table-time-range-label"
                        id="table-time-range-select"
                        value={tableTimeRange}
                        onChange={handleTableTimeRangeChange}
                        label="Time Range"
                      >
                        <MenuItem value={1}>Last Hour</MenuItem>
                        <MenuItem value={6}>Last 6 Hours</MenuItem>
                        <MenuItem value={24}>Last 24 Hours</MenuItem>
                        <MenuItem value={48}>Last 48 Hours</MenuItem>
                        <MenuItem value={120}>Last 5 Days</MenuItem>
                        <MenuItem value={720}>Last Month</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      variant={showAdvancedFilters ? "contained" : "outlined"}
                      startIcon={<Tune />}
                      endIcon={showAdvancedFilters ? <ExpandLess /> : <ExpandMore />}
                      onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                      sx={{
                        height: '40px',
                        ...(showAdvancedFilters && {
                          background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
                        })
                      }}
                    >
                      Advanced
                      {advancedRules.length > 0 && (
                        <Chip
                          label={advancedRules.length}
                          size="small"
                          sx={{ ml: 1, height: 20, minWidth: 20, backgroundColor: 'rgba(255,255,255,0.2)' }}
                        />
                      )}
                    </Button>
                  </Grid>
                </Grid>

                {/* Advanced Filters Panel */}
                <Collapse in={showAdvancedFilters}>
                  <Paper
                    sx={{
                      p: 2,
                      mb: 3,
                      backgroundColor: 'rgba(0, 212, 255, 0.05)',
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                      borderRadius: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Advanced Filters
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Match:
                          </Typography>
                          <ToggleButtonGroup
                            value={advancedMatchMode}
                            exclusive
                            onChange={handleAdvancedMatchModeChange}
                            size="small"
                          >
                            <ToggleButton value="all" sx={{ px: 2, py: 0.5 }}>
                              All
                            </ToggleButton>
                            <ToggleButton value="any" sx={{ px: 2, py: 0.5 }}>
                              Any
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                        {advancedRules.length > 0 && (
                          <Button
                            size="small"
                            color="error"
                            onClick={clearAdvancedFilters}
                          >
                            Clear All
                          </Button>
                        )}
                      </Box>
                    </Box>

                    {/* Rules */}
                    {advancedRules.map((rule, index) => (
                      <Box
                        key={rule.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: 1.5,
                          p: 1.5,
                          backgroundColor: 'background.paper',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
                          Rule {index + 1}:
                        </Typography>

                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <InputLabel>Metric</InputLabel>
                          <Select
                            value={rule.metric}
                            label="Metric"
                            onChange={(e) => handleAdvancedRuleChange(rule.id, 'metric', e.target.value)}
                          >
                            {getUniqueMetrics().map(metric => {
                              const config = metricConfig[metric];
                              return config ? (
                                <MenuItem key={metric} value={metric}>
                                  {config.name}
                                </MenuItem>
                              ) : null;
                            })}
                          </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <InputLabel>Operator</InputLabel>
                          <Select
                            value={rule.operator}
                            label="Operator"
                            onChange={(e) => handleAdvancedRuleChange(rule.id, 'operator', e.target.value)}
                          >
                            <MenuItem value=">">{'>'} Greater than</MenuItem>
                            <MenuItem value="<">{'<'} Less than</MenuItem>
                            <MenuItem value=">=">{'>='} Greater or equal</MenuItem>
                            <MenuItem value="<=">{'<='} Less or equal</MenuItem>
                            <MenuItem value="=">{'='} Equal to</MenuItem>
                          </Select>
                        </FormControl>

                        <TextField
                          size="small"
                          type="number"
                          label="Value"
                          value={rule.value}
                          onChange={(e) => handleAdvancedRuleChange(rule.id, 'value', e.target.value)}
                          sx={{ width: 120 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography variant="caption" color="text.secondary">
                                  {metricConfig[rule.metric]?.unit || ''}
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                        />

                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveAdvancedRule(rule.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}

                    {/* Add Rule + Apply Buttons */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Add />}
                        onClick={handleAddAdvancedRule}
                        disabled={getUniqueMetrics().length === 0}
                      >
                        Add Rule
                      </Button>
                      {advancedRules.length > 0 && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={applyAdvancedFilters}
                          disabled={advancedFilterLoading}
                          sx={{ color: '#000000' }}
                        >
                          {advancedFilterLoading ? 'Filtering...' : 'Apply Filters'}
                        </Button>
                      )}
                      {advancedFilterLoading && <CircularProgress size={20} />}
                    </Box>

                    {advancedFilterError && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        {advancedFilterError}
                      </Typography>
                    )}

                    {advancedFilterResults !== null && !advancedFilterLoading && (
                      <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                        Showing {advancedFilterResults.length} filtered results from server
                      </Typography>
                    )}

                    {advancedRules.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        No rules defined. Click "Add Rule" to filter by metric values (e.g., Temperature {'>'} 30°C).
                      </Typography>
                    )}
                  </Paper>
                </Collapse>

                {/* Table */}
                {!filterStation ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Select a station to view measurements
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Use the "Select Station" dropdown above to load timeseries data.
                    </Typography>
                  </Box>
                ) : tableLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                      Loading measurements...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Station</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getFilteredTableData()
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index) => (
                              <TableRow key={`${row.stationUuid}-${row.timestamp}-${row.metric}-${index}`}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LocationOn sx={{ mr: 1, fontSize: 18, color: '#00d4ff' }} />
                                    {row.stationName}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={row.metricName}
                                    size="small"
                                    sx={{
                                      backgroundColor: `${metricConfig[row.metric]?.color}20`,
                                      color: metricConfig[row.metric]?.color,
                                      fontWeight: 500,
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {typeof row.value === 'number' ? row.value.toFixed(2) : row.value}
                                  </Typography>
                                </TableCell>
                                <TableCell>{row.unit}</TableCell>
                                <TableCell>
                                  {new Date(row.timestamp).toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                  })}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {getFilteredTableData().length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          No measurements found matching the current filters.
                        </Typography>
                      </Box>
                    )}

                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      component="div"
                      count={getFilteredTableData().length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Compare Stations Tab */}
        {tabValue === 3 && (
          <StationCompare stations={stations} />
        )}

        {/* Groups Tab */}
        {tabValue === 4 && (
          <GroupsTab />
        )}

        {/* Advanced Tasks Tab (admin only) */}
        {stats?.is_admin && tabValue === 5 && (
          <Box>
            <Card sx={{
              background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.8) 0%, rgba(22, 33, 62, 0.8) 100%)',
              border: '1px solid rgba(0, 212, 255, 0.2)',
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <AdminPanelSettings sx={{ fontSize: 32, color: '#00d4ff' }} />
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Bulk Station Import
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload a CSV file to create multiple stations at once. Maximum 1000 stations per import.
                </Typography>

                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mb: 3 }}
                  onClick={async () => {
                    try {
                      const response = await api.get('/stations/bulk-import/template/', { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = 'bulk-stations-template.csv';
                      link.click();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      setBulkImportError('Failed to download template.');
                    }
                  }}
                >
                  Download CSV Template
                </Button>

                {/* Upload Area */}
                <Box
                  sx={{
                    border: '2px dashed rgba(0, 212, 255, 0.3)',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#00d4ff',
                      backgroundColor: 'rgba(0, 212, 255, 0.05)',
                    },
                    mb: 3,
                  }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const file = e.dataTransfer.files[0];
                    if (file) handleBulkImport(file);
                  }}
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.csv';
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) handleBulkImport(file);
                    };
                    input.click();
                  }}
                >
                  <CloudUpload sx={{ fontSize: 48, color: 'rgba(0, 212, 255, 0.5)', mb: 1 }} />
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    {bulkImportLoading ? 'Importing...' : 'Drag & drop CSV file here or click to browse'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    CSV format • Max 1MB • Max 1000 stations
                  </Typography>
                </Box>

                {bulkImportLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <CircularProgress />
                  </Box>
                )}

                {/* Success Result */}
                {bulkImportResult && !bulkImportResult.errors && (
                  <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {bulkImportResult.message}
                    </Typography>
                    {bulkImportResult.stations && (
                      <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                        {bulkImportResult.stations.map((s: any, i: number) => (
                          <Typography key={i} variant="caption" display="block">
                            {s.name} — UUID: {s.uuid}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Alert>
                )}

                {/* Error Result */}
                {bulkImportError && (
                  <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorOutline />}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {bulkImportError}
                    </Typography>
                  </Alert>
                )}

                {/* Validation Errors per row */}
                {bulkImportResult?.errors && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Validation failed on {bulkImportResult.errors.length} of {bulkImportResult.total_rows} rows:
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {bulkImportResult.errors.map((err: any, i: number) => (
                        <Typography key={i} variant="caption" display="block" sx={{ color: 'warning.dark' }}>
                          Row {err.row}: {typeof err.error === 'string' ? err.error : JSON.stringify(err.error)}
                        </Typography>
                      ))}
                    </Box>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Station Details Dialog */}
        <StationDetails
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
          stationUuid={selectedStationUuid}
          initialTab={initialDialogTab}
        />

        {/* Add Station Dialog */}
        <AddStationDialog
          open={addStationDialogOpen}
          onClose={() => {
            setAddStationDialogOpen(false);
            setAddStationLocation(undefined);
          }}
          onSubmit={handleCreateStation}
          initialLocation={addStationLocation}
        />

        <StationConfigDialog
          open={configDialogOpen}
          onClose={() => setConfigDialogOpen(false)}
          station={selectedConfigStation}
          onEdit={handleEditStation}
          onDelete={handleDeleteStation}
          onExport={handleExportStation}
        />

        {/* Warning Snackbar for Configure Button */}
        <Snackbar
          open={warningSnackbarOpen}
          autoHideDuration={4000}
          onClose={() => setWarningSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            top: '80px !important',
            zIndex: 10002
          }}
        >
          <Alert
            onClose={() => setWarningSnackbarOpen(false)}
            severity="warning"
            variant="filled"
            sx={{ width: '100%' }}
          >
            Please select a station first before configuring
          </Alert>
        </Snackbar>

        {/* Beta Testing Notice Modal */}
        <Dialog
          open={betaModalOpen}
          onClose={handleCloseBetaModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.98) 0%, rgba(22, 33, 62, 0.98) 100%)',
              border: '1px solid rgba(0, 212, 255, 0.3)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Science sx={{ color: '#00d4ff', fontSize: 32 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>
                Beta Testing
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseBetaModal}
              sx={{
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 4 }}>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8, mb: 2 }}>
              We are currently in <strong style={{ color: '#00d4ff' }}>Beta Testing</strong> period.
              We are actively improving features, performance, and stability, and your feedback helps us move faster.
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8, mb: 2 }}>
              If you run into any issues or have suggestions, let us know... we are listening.
            </Typography>
            <Typography variant="body1" sx={{ color: '#00d4ff', fontWeight: 600, lineHeight: 1.8 }}>
              Public release is coming soon!
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={handleCloseBetaModal}
                sx={{
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
                  color: '#000',
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #00e5ff 0%, #00b8e6 100%)',
                  }
                }}
              >
                Got it!
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Dashboard;